import type { Contract } from '../types';
import type { CompanyDashboardFinance } from '../services/api';
import { isPayableContract } from './pendingAttestation';
import { timelineContracts } from './timelineContracts';

const contractAmountValue = (contract: Contract) =>
  parseFloat(String(contract.contractAmount || '0').replace(/[^0-9.-]/g, '')) || 0;

export interface CompanyKeyFigures {
  /** Paid out for attested stages, on the customer's funds (before fees) —
   * the same basis as "Utbetalt till er" in the project page's contract frames.
   * Null until the finance summary has loaded. */
  paidOutSek: number | null;
  /** Paid in and held until the customer attests ("Inbetalt, ej utbetalt"). */
  securedSek: number | null;
  /** What customers still have to pay in on signed contracts. */
  awaitingPaymentSek: number;
  /** Unsigned contracts that have an amount, and their total value. Drafts
   * without an amount yet (0 kr) are left out, so the count matches the sum.
   * A contract the customer sent back counts: it waits on the company. */
  bidCount: number;
  bidValueSek: number;
  /** Of those, the ones where the customer has the next step: reviewing a sent
   * draft, or signing after the company has signed. The rest wait on the
   * company (send the draft, revise one the customer sent back, or sign). */
  bidsAwaitingCustomer: number;
}

const awaitsCustomer = (contract: Contract) =>
  contract.draftStatus === 'draft_sent'
  || ((contract.companySigned || contract.draftStatus === 'signed_by_company') && !contract.personSigned);

/**
 * The company's key figures on the start page. Every figure is an existing
 * value: the API's finance summary (paid out, secured) and the sums the start
 * page has always shown (awaiting payment, bid value). No new calculations.
 */
export function companyKeyFigures(allContracts: Contract[], finance: CompanyDashboardFinance | null): CompanyKeyFigures {
  // Only the version of each contract that applies now, as in the contract
  // frames: a replaced version or an old ÄTA draft must not count twice.
  const contracts = timelineContracts(allContracts);
  const bids = contracts.filter(contract => !isPayableContract(contract) && contractAmountValue(contract) > 0);
  return {
    paidOutSek: finance ? finance.completedVolumeOre / 100 : null,
    securedSek: finance ? finance.reservedAmountOre / 100 : null,
    awaitingPaymentSek: contracts.filter(isPayableContract)
      .reduce((sum, contract) => sum + (contract.remainingAmount || 0), 0),
    bidCount: bids.length,
    bidValueSek: bids.reduce((sum, contract) => sum + contractAmountValue(contract), 0),
    bidsAwaitingCustomer: bids.filter(awaitsCustomer).length
  };
}
